import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save } from "lucide-react";
import { AdminModal, AdminField, AdminTable, AdminPageHeader, ImageUploadField, toSlug } from "./AdminBits";
import RichTextEditor from "@/components/common/RichTextEditor";

const RichField = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs font-medium text-[#4B5563] mb-1 uppercase tracking-wider">{label}</label>
    <RichTextEditor value={value} onChange={onChange} testid={testid}/>
  </div>
);

const emptyNews = {
  title_tr: "", title_en: "", title_ar: "", slug: "",
  summary_tr: "", summary_en: "", summary_ar: "",
  content_tr: "", content_en: "", content_ar: "",
  cover_image: "", category: "genel", tags: [],
  published_at: "", seo_title: "", seo_description: "", status: "published",
};

export function AdminNews() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyNews);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/news?published_only=false&limit=200");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setF(emptyNews); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setF({ ...emptyNews, ...r }); setOpen(true); };
  const del = async (r) => { await api.delete(`/news/${r.id}`); toast.success("Silindi"); load(); };

  const onChange = (e) => setF(prev => {
    const next = { ...prev, [e.target.name]: e.target.value };
    if (e.target.name === "title_tr" && !editing) next.slug = toSlug(e.target.value);
    return next;
  });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/news/${editing.id}`, f);
      else await api.post("/news", f);
      toast.success("Kaydedildi");
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  const cols = [
    { key: "title_tr", label: "Başlık", render: r => <div><div className="font-medium">{r.title_tr}</div><div className="text-xs text-[#4B5563]">/{r.slug}</div></div> },
    { key: "category", label: "Kategori" },
    { key: "published_at", label: "Tarih", render: r => r.published_at ? new Date(r.published_at).toLocaleDateString("tr-TR") : "-" },
    { key: "status", label: "Durum" },
  ];

  return (
    <div data-testid="admin-news">
      <AdminPageHeader title="Haberler" subtitle={`${items.length} haber`} action={<button onClick={startCreate} className="btn-primary !py-2 !px-4 text-sm" data-testid="btn-new-news"><Plus className="w-4 h-4"/> Yeni Haber</button>}/>
      <AdminTable columns={cols} rows={items} onEdit={startEdit} onDelete={del} testid="table-news"/>
      <AdminModal open={open} onClose={() => setOpen(false)} title={editing ? "Haberi Düzenle" : "Yeni Haber"} testid="news-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <AdminField label="Başlık (TR)" name="title_tr" value={f.title_tr} onChange={onChange} required testid="nf-title-tr"/>
            <AdminField label="Slug" name="slug" value={f.slug} onChange={onChange} required testid="nf-slug"/>
            <AdminField label="Başlık (EN)" name="title_en" value={f.title_en} onChange={onChange} testid="nf-title-en"/>
            <AdminField label="Kategori" name="category" value={f.category} onChange={onChange} testid="nf-category"/>
          </div>
          <ImageUploadField label="Kapak Görseli" value={f.cover_image} onChange={(v) => setF(p => ({ ...p, cover_image: v }))} testid="nf-cover"/>
          <AdminField label="Özet (TR)" name="summary_tr" value={f.summary_tr} onChange={onChange} isTextArea rows={2} testid="nf-sum-tr"/>
          <RichField label="İçerik (TR) — Zengin Metin" value={f.content_tr} onChange={(v) => setF(p => ({ ...p, content_tr: v }))} testid="nf-content-tr"/>
          <RichField label="İçerik (EN)" value={f.content_en} onChange={(v) => setF(p => ({ ...p, content_en: v }))} testid="nf-content-en"/>
          <RichField label="İçerik (AR)" value={f.content_ar} onChange={(v) => setF(p => ({ ...p, content_ar: v }))} testid="nf-content-ar"/>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline !py-2 !px-4 text-sm">İptal</button>
            <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm" data-testid="nf-submit"><Save className="w-4 h-4"/> {saving ? "..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}

const emptyAnn = { title_tr: "", title_en: "", title_ar: "", slug: "", summary_tr: "", summary_en: "", summary_ar: "", content_tr: "", content_en: "", content_ar: "", published_at: "", status: "published" };

export function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyAnn);
  const [saving, setSaving] = useState(false);

  const load = async () => { const { data } = await api.get("/announcements?published_only=false&limit=200"); setItems(data); };
  useEffect(() => { load(); }, []);
  const startCreate = () => { setEditing(null); setF(emptyAnn); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setF({ ...emptyAnn, ...r }); setOpen(true); };
  const del = async (r) => { await api.delete(`/announcements/${r.id}`); toast.success("Silindi"); load(); };
  const onChange = (e) => setF(prev => { const next = { ...prev, [e.target.name]: e.target.value }; if (e.target.name === "title_tr" && !editing) next.slug = toSlug(e.target.value); return next; });
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { if (editing) await api.put(`/announcements/${editing.id}`, f); else await api.post("/announcements", f); toast.success("Kaydedildi"); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };
  const cols = [
    { key: "title_tr", label: "Başlık", render: r => <div><div className="font-medium">{r.title_tr}</div><div className="text-xs text-[#4B5563]">/{r.slug}</div></div> },
    { key: "published_at", label: "Tarih", render: r => r.published_at ? new Date(r.published_at).toLocaleDateString("tr-TR") : "-" },
    { key: "status", label: "Durum" },
  ];
  return (
    <div data-testid="admin-announcements">
      <AdminPageHeader title="Duyurular" subtitle={`${items.length} duyuru`} action={<button onClick={startCreate} className="btn-primary !py-2 !px-4 text-sm" data-testid="btn-new-ann"><Plus className="w-4 h-4"/> Yeni Duyuru</button>}/>
      <AdminTable columns={cols} rows={items} onEdit={startEdit} onDelete={del} testid="table-ann"/>
      <AdminModal open={open} onClose={() => setOpen(false)} title={editing ? "Duyuruyu Düzenle" : "Yeni Duyuru"} testid="ann-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <AdminField label="Başlık (TR)" name="title_tr" value={f.title_tr} onChange={onChange} required testid="af-title-tr"/>
            <AdminField label="Slug" name="slug" value={f.slug} onChange={onChange} required testid="af-slug"/>
          </div>
          <AdminField label="Özet (TR)" name="summary_tr" value={f.summary_tr} onChange={onChange} isTextArea rows={2} testid="af-sum"/>
          <RichField label="İçerik (TR) — Zengin Metin" value={f.content_tr} onChange={(v) => setF(p => ({ ...p, content_tr: v }))} testid="af-content"/>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline !py-2 !px-4 text-sm">İptal</button>
            <button type="submit" disabled={saving} className="btn-primary !py-2 !px-4 text-sm" data-testid="af-submit"><Save className="w-4 h-4"/> {saving ? "..." : "Kaydet"}</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}

const emptyBoard = { name: "", role_title: "", professional_title: "", bio: "", photo: "", linkedin_url: "", email: "", display_order: 100, status: "active" };

export function AdminBoard() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyBoard);
  const load = async () => { const { data } = await api.get("/board-members"); setItems(data); };
  useEffect(() => { load(); }, []);
  const startCreate = () => { setEditing(null); setF(emptyBoard); setOpen(true); };
  const startEdit = (r) => { setEditing(r); setF({ ...emptyBoard, ...r }); setOpen(true); };
  const del = async (r) => { await api.delete(`/board-members/${r.id}`); toast.success("Silindi"); load(); };
  const onChange = (e) => setF(prev => ({ ...prev, [e.target.name]: e.target.name === "display_order" ? parseInt(e.target.value) || 0 : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    try { if (editing) await api.put(`/board-members/${editing.id}`, f); else await api.post("/board-members", f); toast.success("Kaydedildi"); setOpen(false); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };
  const cols = [
    { key: "name", label: "Ad Soyad", render: r => <div className="flex items-center gap-3">{r.photo ? <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover"/> : <div className="w-10 h-10 rounded-full bg-[#EAF7F0] flex items-center justify-center text-[#0F5132] font-bold text-sm">{r.name?.[0]}</div>}<div>{r.name}</div></div> },
    { key: "role_title", label: "Görev" },
    { key: "professional_title", label: "Unvan" },
    { key: "display_order", label: "Sıra" },
  ];
  return (
    <div data-testid="admin-board">
      <AdminPageHeader title="Yönetim Kurulu" subtitle={`${items.length} üye`} action={<button onClick={startCreate} className="btn-primary !py-2 !px-4 text-sm" data-testid="btn-new-board"><Plus className="w-4 h-4"/> Yeni Üye</button>}/>
      <AdminTable columns={cols} rows={items} onEdit={startEdit} onDelete={del} testid="table-board"/>
      <AdminModal open={open} onClose={() => setOpen(false)} title={editing ? "Üyeyi Düzenle" : "Yeni Üye"} testid="board-modal">
        <form onSubmit={save} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <AdminField label="Ad Soyad" name="name" value={f.name} onChange={onChange} required testid="bf-name"/>
            <AdminField label="Görev" name="role_title" value={f.role_title} onChange={onChange} required testid="bf-role"/>
            <AdminField label="Mesleki Unvan" name="professional_title" value={f.professional_title} onChange={onChange} testid="bf-prof"/>
            <AdminField label="LinkedIn" name="linkedin_url" value={f.linkedin_url} onChange={onChange} testid="bf-linkedin"/>
            <AdminField label="E-posta" name="email" value={f.email} onChange={onChange} testid="bf-email"/>
            <AdminField label="Sıra" name="display_order" value={f.display_order} onChange={onChange} type="number" testid="bf-order"/>
          </div>
          <ImageUploadField label="Fotoğraf" value={f.photo} onChange={(v) => setF(p => ({ ...p, photo: v }))} testid="bf-photo"/>
          <AdminField label="Biyografi" name="bio" value={f.bio} onChange={onChange} isTextArea rows={3} testid="bf-bio"/>
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline !py-2 !px-4 text-sm">İptal</button>
            <button type="submit" className="btn-primary !py-2 !px-4 text-sm" data-testid="bf-submit"><Save className="w-4 h-4"/> Kaydet</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
